/**
 * `createAuditSource` — the tenancy's raw call trail (`management-api`
 * `GET /audit`), which backs Activity → Audit.
 *
 * The other half of the Activity page. The notification inbox is domain
 * EVENTS: what a service chose to announce because somebody is waiting for it.
 * This is what actually ran — every handler's decorator writes one ActivityLog
 * row per invocation, reads included, about ten per page load. Until this route
 * existed nothing outside the team audit feed could read them: a sandbox
 * tenancy held 408,000 rows in two months, visible nowhere.
 *
 * **The tenancy is not on the wire.** The partition comes from the verified
 * auth context server-side; naming another is not a filter this route offers,
 * so there is no `orgId` parameter to pass and no way to point it elsewhere.
 *
 * Canonical `/audit` is mapped onto `/management/v0/audit` by
 * `RESOURCE_REWRITES` (the `.management.` zone, same as `/usage`).
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

const AUDIT_PATH = "/audit";

/** One recorded invocation, in the fields the tenant's own surface may show. */
export interface AuditEntry {
  id: string;
  /** Epoch SECONDS (the table's range key), or null on a row that lost it. */
  at: number | null;
  /** The deployed Lambda's name, e.g. `sandbox-pdx-management-api-get-usage`. */
  function: string;
  actorUserId: string | null;
  /** Which client made the call — `dashboard`, `management`, an API key… */
  application: string | null;
  success: boolean;
  statusCode: number | null;
  durationMs: number | null;
  correlationId: string | null;
  coldStart: boolean;
}

/**
 * Whether this deployment can read the trail at all.
 *
 * Mirrors `MeteringStatus` on `GET /usage`, for the same reason: an unwired
 * ActivityLog table and a tenancy that genuinely made no calls otherwise come
 * back as identical empty lists, and a surface that cannot tell them apart says
 * "nothing happened" about a platform that is not recording.
 *
 * `reason` is `"table_unwired"` (the table is owned by `internal-api-data` and
 * wired opportunistically), `"global_tenancy"` (this session resolved to the
 * shared tier, whose partition holds every org's unscoped calls — it is not
 * this tenant's trail) or `"no_tenancy"`.
 */
export interface AuditStatus {
  available: boolean;
  reason: string | null;
  /**
   * The server spent its page budget with rows still behind it. A filtered read
   * (failures only) over a tenancy whose traffic is almost entirely successful
   * does this routinely — a short page is NOT the end of the trail.
   */
  truncated: boolean;
}

export interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
  status: AuditStatus;
}

export interface AuditQuery {
  limit?: number;
  cursor?: string | null;
  /** Only invocations that did not succeed. Applied server-side after the read. */
  failuresOnly?: boolean;
  /** Match invocations whose function name starts with this. */
  functionPrefix?: string | null;
}

export interface AuditSource {
  getAudit(query?: AuditQuery): Promise<AuditPage>;
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/**
 * A wire number that may arrive as a string.
 *
 * The rows are DynamoDB attributes, which come back as `Decimal`, and the
 * response encoder renders a `Decimal` as a STRING. The backend's projection
 * coerces them, and this coerces again rather than trusting it: a duration that
 * reaches a sort comparator as text orders 9 after 100.
 */
function asNumber(raw: unknown): number | null {
  if (typeof raw === "boolean") return null;
  const n = typeof raw === "string" && raw.trim() !== "" ? Number(raw) : raw;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function asText(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim() !== "" ? raw : null;
}

export function entryFromWire(raw: unknown): AuditEntry {
  const wire = asRecord(raw);
  return {
    id: String(wire.id ?? ""),
    at: asNumber(wire.at),
    function: typeof wire.function === "string" ? wire.function : "",
    actorUserId: asText(wire.actor_user_id),
    application: asText(wire.application),
    // Absent means the model default (the call succeeded), not a failure: a row
    // written before this field existed is not evidence of an error.
    success: wire.success !== false,
    statusCode: asNumber(wire.status_code),
    durationMs: asNumber(wire.duration_ms),
    correlationId: asText(wire.correlation_id),
    coldStart: wire.cold_start === true,
  };
}

/**
 * An older deploy sends no `audit` block. Assume AVAILABLE there, the same way
 * `meteringFromWire` does — that deployment's empty list really did mean "no
 * calls", and defaulting to unavailable would put a "not recording" banner on a
 * working install.
 */
function statusFromWire(raw: unknown): AuditStatus {
  const wire = asRecord(raw);
  if (typeof wire.available !== "boolean") return { available: true, reason: null, truncated: false };
  return {
    available: wire.available,
    reason: asText(wire.reason),
    truncated: wire.truncated === true,
  };
}

export function createAuditSource(client: ApiClient): AuditSource {
  return {
    async getAudit(query: AuditQuery = {}): Promise<AuditPage> {
      const res = await client.get<unknown>(AUDIT_PATH, {
        query: {
          limit: query.limit ?? null,
          cursor: query.cursor ?? null,
          failures_only: query.failuresOnly ? "true" : null,
          function: query.functionPrefix ?? null,
        },
        // The point of this surface is what happened JUST now — a page load
        // whose own rows are still in the client cache would show a trail that
        // stops before the call that asked for it.
        cacheTtlMs: 0,
      });
      const body = asRecord(unwrapData(res));
      return {
        entries: (Array.isArray(body.items) ? body.items : []).map(entryFromWire),
        nextCursor: asText(body.next_cursor),
        status: statusFromWire(body.audit),
      };
    },
  };
}
